import { useCallback, useEffect, useRef, useState } from 'react'

import {
  ChatActor,
  type ChatMessageI,
  type IncidentContextI,
  SOP_AI_ASSIST_EVENT,
} from '../types'
import {
  type CannedExchangeI,
  SALES_CTA_RESPONSES,
  buildDhoConversation,
} from './salesDemoConversation'

const TYPING_DELAY_MS = 1500
const BOT_RESPONSE_DELAY_MS = 2500

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

export const useSalesDemoChatbot = (
  incidentContext: IncidentContextI | null
) => {
  const [messages, setMessages] = useState<ChatMessageI[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([])

  // isAutoPlaying as state drives re-renders (disables input, shows indicator).
  // autoPlayRef mirrors it so async callbacks in startAutoPlay/playExchange
  // read the current value instead of a stale closure capture.
  const [isAutoPlaying, setIsAutoPlaying] = useState(false)
  const autoPlayRef = useRef(false)

  const hasPlayedRef = useRef(false)
  const conversationRef = useRef<ReturnType<typeof buildDhoConversation>>([])
  const messageIdRef = useRef(0)
  const ctaIndexRef = useRef(0)

  const nextId = () => `sales-msg-${++messageIdRef.current}`

  const nextCtaResponse = () => {
    const idx = ctaIndexRef.current % SALES_CTA_RESPONSES.length
    ctaIndexRef.current++
    return SALES_CTA_RESPONSES[idx] ?? SALES_CTA_RESPONSES[0]!
  }

  // wipe everything and start fresh for a new incident
  const resetChatState = () => {
    setMessages([])
    setSuggestedQuestions([])
    setIsAutoPlaying(false)
    autoPlayRef.current = false
    hasPlayedRef.current = false
    ctaIndexRef.current = 0
  }

  useEffect(() => {
    resetChatState()
    if (incidentContext) {
      conversationRef.current = buildDhoConversation(incidentContext)
    }
  }, [incidentContext?.incident_id])

  // Ref indirection: the event listener captures startAutoPlayRef once,
  // avoiding re-registration every time startAutoPlay's identity changes.
  const startAutoPlayRef = useRef<() => void>(() => {})

  // Listen for CTA button click from FeedButtons (only when active)
  useEffect(() => {
    if (!incidentContext) return
    const incidentId = incidentContext.incident_id
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail?.incidentId && detail.incidentId !== incidentId) return
      startAutoPlayRef.current()
    }
    window.addEventListener(SOP_AI_ASSIST_EVENT, handler)
    return () => window.removeEventListener(SOP_AI_ASSIST_EVENT, handler)
  }, [incidentContext?.incident_id])

  const appendMessage = useCallback((msg: ChatMessageI) => {
    setMessages((prev) => [...prev, msg])
  }, [])

  const playExchange = useCallback(
    async (exchange: CannedExchangeI) => {
      appendMessage({
        id: nextId(),
        text: exchange.question,
        actor: ChatActor.USER,
        timestamp: new Date(),
      })

      setIsLoading(true)
      await delay(BOT_RESPONSE_DELAY_MS)

      if (!autoPlayRef.current) {
        setIsLoading(false)
        return
      }

      setIsLoading(false)
      appendMessage({
        id: nextId(),
        text: exchange.answer,
        actor: ChatActor.BOT,
        sources: exchange.sources,
        suggestedQuestions: exchange.suggestedQuestions,
        timestamp: new Date(),
      })
      setSuggestedQuestions(exchange.suggestedQuestions)
    },
    [appendMessage]
  )

  const startAutoPlay = useCallback(async () => {
    if (!incidentContext || isAutoPlaying || hasPlayedRef.current) return

    setIsAutoPlaying(true)
    autoPlayRef.current = true
    hasPlayedRef.current = true
    const conversation = conversationRef.current

    let isFirst = true
    for (const exchange of conversation) {
      if (!autoPlayRef.current) break

      if (!isFirst) {
        await delay(TYPING_DELAY_MS)
        if (!autoPlayRef.current) break
      }
      isFirst = false

      await playExchange(exchange)
    }

    setIsAutoPlaying(false)
  }, [incidentContext, isAutoPlaying, playExchange])

  startAutoPlayRef.current = startAutoPlay

  // Manual send: canned matches play the scripted answer, anything else
  // returns a rotating sales CTA nudging toward a full demo.
  const sendMessage = useCallback(
    async (question: string) => {
      if (!incidentContext || !question.trim() || isLoading || isAutoPlaying)
        return

      setSuggestedQuestions([])

      appendMessage({
        id: nextId(),
        text: question.trim(),
        actor: ChatActor.USER,
        timestamp: new Date(),
      })

      const matchedExchange = conversationRef.current.find(
        (ex) => ex.question.toLowerCase() === question.trim().toLowerCase()
      )

      setIsLoading(true)
      await delay(BOT_RESPONSE_DELAY_MS)
      setIsLoading(false)

      if (matchedExchange) {
        appendMessage({
          id: nextId(),
          text: matchedExchange.answer,
          actor: ChatActor.BOT,
          sources: matchedExchange.sources,
          suggestedQuestions: matchedExchange.suggestedQuestions,
          timestamp: new Date(),
        })
        setSuggestedQuestions(matchedExchange.suggestedQuestions)
      } else {
        appendMessage({
          id: nextId(),
          text: nextCtaResponse(),
          actor: ChatActor.BOT,
          timestamp: new Date(),
        })
      }
    },
    [incidentContext, isLoading, isAutoPlaying, appendMessage]
  )

  return {
    messages,
    isLoading,
    suggestedQuestions,
    error: null,
    sendMessage,
    startAutoPlay,
    isAutoPlaying,
  }
}
